import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectEditingCampaignCountry } from 'state/campaigns'
import {
    makeLocationOptionsByCountrySelector,
    requestLocationOptions,
    selectCityTypesByEditingCampaignCountry,
} from 'state/constants'
import { useField, useFormikContext } from 'formik'
import Divider from 'components/Shared/Divider'
import Icon from 'components/Icon/Icon'
import { LocationRequirementContainer } from './styles'
import { RequirementCardContainer } from 'components/Shared/Fields/styles'
import Text from 'particles/Text'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { useLocationTranslation } from 'hooks'
import RectangleButton from 'particles/Button/RectangleButton'
import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { generateUuid } from 'services/uuid'

export interface LocationDataFormat {
    id: number | null
    name: string | null
    uuid: string
}
export interface LocationData {
    [key: string]: LocationDataFormat
}

interface Rank1SelectProps {
    label: string
    name: string
    idName: string
    childIdName?: string
    categoryName: string
}

const Rank1Select: React.FC<Rank1SelectProps> = ({ label, name, idName, childIdName, categoryName }) => {
    const [t, i18n] = useTranslation()
    const { setFieldValue } = useFormikContext()
    const [field] = useField<LocationData>(name)
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const locationOptionsByCountrySelector = useMemo(() => makeLocationOptionsByCountrySelector('edit', idName), [])
    const { data: options } = useSelector(locationOptionsByCountrySelector)
    const optionUuid = field?.value[idName]?.uuid

    const menuItemComponent = useMemo(() => {
        const menuItems: React.ReactNode[] = []
        if (!options) return null
        options.map((option, index) => {
            if (index === 0) {
                menuItems.push(
                    <MenuItem.Title
                        text={
                            option.is_major
                                ? i18n.language === 'vi'
                                    ? `${categoryName} ${t('campaignEditor.requirement.selectMajorMenuSection')} `
                                    : `${t('campaignEditor.requirement.selectMajorMenuSection')} ${categoryName}`
                                : `${t('campaignEditor.requirement.selectAllMenuSection')} ${categoryName}`
                        }
                    />,
                )
            }
            menuItems.push(<MenuItem.Text text={option.name} value={option.id} tabIndex={index} />)
        })
        return menuItems
    }, [options, t])
    const selectedOption = useMemo(() => {
        let currentValue = null
        if (options) {
            currentValue = options.find(option => field.value && field.value[idName]?.id === option.id)
        }
        if (currentValue === undefined) {
            currentValue = null
        }
        return currentValue
    }, [options, field.value])

    if (!editingCampaignCountry) return null
    return (
        <CampaignEditorInputFieldContainer>
            <Dropdown
                label={label}
                placeholder={t('placeholderText.clickToSelect')}
                value={selectedOption?.id}
                onChange={(value): void => {
                    const currentValue = options?.find(option => value === option.id)
                    const updatedField = {
                        ...field.value[idName],
                        id: currentValue?.id,
                        name: currentValue?.name,
                    }
                    setFieldValue(`${field.name}.${idName}` as never, updatedField)
                    if (childIdName) {
                        const updatedChildValue = { ...field.value[childIdName], id: null, name: null }
                        setFieldValue(`${field.name}.${childIdName}` as never, updatedChildValue, false)
                    }
                }}
                isRequired
                identifier={`${optionUuid}_${fieldIdentifiers.rank1}`}
            >
                {menuItemComponent}
            </Dropdown>
        </CampaignEditorInputFieldContainer>
    )
}

interface Rank2SelectProps {
    label: string
    name: string
    parentIdName: string
    idName: string
    categoryName: string
}

const Rank2Select: React.FC<Rank2SelectProps> = ({ label, name, parentIdName, idName, categoryName }) => {
    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()
    const { setFieldValue } = useFormikContext()
    const [field] = useField<LocationData>(name)
    const parent = field.value ? field?.value[parentIdName]?.id : null
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const locationOptionsByCountrySelector = useMemo(
        () => makeLocationOptionsByCountrySelector('edit', idName, parent),
        [idName, parent],
    )
    const { isLoading: isOptionsLoading, data: options } = useSelector(locationOptionsByCountrySelector)
    const optionUuid = field?.value[idName]?.uuid

    const menuItemComponent = useMemo(() => {
        const menuItems: React.ReactNode[] = []
        if (!options) return null
        options.forEach((option, index) => {
            if (index === 0) {
                menuItems.push(
                    <MenuItem.Title
                        text={
                            i18n.language === 'vi'
                                ? `${t('campaignEditor.requirement.selectAllMenuSection')}`
                                : `${t('campaignEditor.requirement.selectAllMenuSection')} ${categoryName}`
                        }
                    />,
                )
            }
            menuItems.push(<MenuItem.Text text={option.name} value={option.id} tabIndex={index} />)
        })
        return menuItems
    }, [options, t])

    useEffect(() => {
        if (editingCampaignCountry && parent && !options && !isOptionsLoading) {
            dispatch(
                requestLocationOptions({
                    country: editingCampaignCountry,
                    type_id_name: idName,
                    parent,
                }),
            )
        }
    }, [editingCampaignCountry, parent, options, isOptionsLoading, idName])

    const selectedOption = useMemo(() => {
        let currentValue = null
        if (options) {
            currentValue = options.find(location => field.value && field.value[idName]?.id === location.id)
        }
        if (currentValue === undefined) {
            currentValue = null
        }

        return currentValue
    }, [options, field.value])

    if (!editingCampaignCountry) return null
    return (
        <CampaignEditorInputFieldContainer>
            <Dropdown
                label={label}
                placeholder={t('placeholderText.clickToSelect')}
                value={selectedOption?.id}
                onChange={(value): void => {
                    const currentValue = options?.find(option => value === option.id)
                    const updatedField = {
                        ...field.value[idName],
                        id: currentValue?.id,
                        name: currentValue?.name,
                    }
                    setFieldValue(`${field.name}.${idName}` as never, updatedField)
                }}
                isDisabled={(field.value && !field.value[parentIdName]) || !options}
                identifier={`${optionUuid}_${fieldIdentifiers.rank2}`}
            >
                {menuItemComponent}
            </Dropdown>
        </CampaignEditorInputFieldContainer>
    )
}

interface LocationRequirementRowProps {
    name: string
    rank1: string
    rank2?: string
    showDeleteButton: boolean
    onRemoveLocation: () => void
}

const LocationRequirementRow: React.FC<LocationRequirementRowProps> = ({
    name,
    rank1,
    rank2,
    showDeleteButton,
    onRemoveLocation,
}) => {
    const [field] = useField(name)
    const [t] = useTranslation()
    const rank1Title = useLocationTranslation(rank1)
    const rank2Title = useLocationTranslation(rank2)

    return (
        <div className="drop-down-input-area">
            <div className="drop-down-double-selector">
                <Rank1Select
                    label={rank1Title}
                    name={field.name}
                    idName={rank1}
                    childIdName={rank2}
                    categoryName={rank1Title}
                />
                {rank2 ? (
                    <Rank2Select
                        label={`${rank2Title} ${t('campaignEditor.requirement.optionalFieldTag')}`}
                        name={field.name}
                        parentIdName={rank1}
                        idName={rank2}
                        categoryName={rank2Title}
                    />
                ) : null}
                {showDeleteButton ? <Icon.CloseIcon onClick={onRemoveLocation} className="remove-field-icon" /> : null}
            </div>
            <Divider width="100%" />
        </div>
    )
}

const LocationRequirement: React.FC<{ name: string; onRemove: () => void; isReadOnly?: boolean }> = ({
    name,
    onRemove,
    isReadOnly,
}) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const [field, meta] = useField<LocationData[]>(name)
    const showError = Boolean(meta.error) && typeof meta.error === 'string'
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const cityTypes = useSelector(selectCityTypesByEditingCampaignCountry)
    const rank1CityType = cityTypes.find(city => city.rank === 1)
    const rank2CityType = cityTypes.find(city => city.rank === 2)

    useEffect(() => {
        if (editingCampaignCountry && rank1CityType)
            dispatch(
                requestLocationOptions({
                    country: editingCampaignCountry,
                    type_id_name: rank1CityType.id_name,
                }),
            )
    }, [editingCampaignCountry, rank1CityType])

    const handleClickAdd = (): void => {
        const current = field.value ? field.value : []
        let next: LocationData[] = []
        if (rank1CityType && rank2CityType) {
            next = current.concat([
                {
                    [rank1CityType.id_name]: {
                        id: null,
                        name: null,
                        uuid: generateUuid(),
                    },
                    [rank2CityType.id_name]: {
                        id: null,
                        name: null,
                        uuid: generateUuid(),
                    },
                },
            ])
            setFieldTouched(`${field.name}[${field.value.length - 1}].${rank1CityType.id_name}` as never, true, false)
            setFieldTouched(`${field.name}[${field.value.length - 1}].${rank2CityType.id_name}` as never, true, false)
        } else if (rank1CityType && !rank2CityType) {
            next = current.concat([
                {
                    [rank1CityType.id_name]: {
                        id: null,
                        name: null,
                        uuid: generateUuid(),
                    },
                },
            ])
            setFieldTouched(`${field.name}[${field.value.length - 1}].${rank1CityType.id_name}` as never, true, false)
        }
        setFieldValue(field.name as never, next)
    }

    const handleClickRemove = (index: number): void => {
        const next = field.value ? [...field.value] : []
        next.splice(index, 1)
        setFieldValue(field.name as never, next)
    }

    if (!rank1CityType || !editingCampaignCountry) return null

    const locations = field.value ? field.value : []
    return (
        <RequirementCardContainer isCard>
            <LocationRequirementContainer>
                <div className="field-title field-bigger-title ">
                    <Text scale="bodyM700">{t('campaignEditor.requirement.locationHeader')} *</Text>
                    {!isReadOnly && <Icon.CloseIcon onClick={onRemove} />}
                </div>
                <Divider />
                {locations.map((_: LocationData, index: number) => (
                    <LocationRequirementRow
                        key={index}
                        name={`${name}[${index}]`}
                        onRemoveLocation={(): void => handleClickRemove(index)}
                        rank1={rank1CityType.id_name}
                        rank2={rank2CityType ? rank2CityType.id_name : undefined}
                        showDeleteButton={locations.length > 1 && !isReadOnly}
                    />
                ))}
                <div className="error">{showError && meta.error}</div>
                {!isReadOnly && (
                    <div className="add-field-button">
                        <Text scale="bodyM500">{t('campaignEditor.requirement.addLocation')}</Text>
                        <RectangleButton
                            form="outlined"
                            size="s"
                            label={t('button.addLocation')}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                e.preventDefault()
                                handleClickAdd()
                            }}
                            icon="plus"
                            className="width-fit-content"
                        />
                    </div>
                )}
            </LocationRequirementContainer>
        </RequirementCardContainer>
    )
}

export default LocationRequirement
